import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect
} from 'react';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { Input, Select, Spinner, Toast, Button } from '@agconnections/grow-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ToggleButton from 'components/ToggleButton';
import { FieldArray, useFormikContext } from 'formik';
import infoIcon from 'assets/info.svg';
import PropTypes from 'prop-types';
import { Context } from 'components/Store';
import PortalModal from 'components/Modals/PortalModal';
import GrowUIFormField from 'components/Forms/GrowUIFormField';
import FormattedNumber from 'components/FormattedNumber';
import { calculateProductValues } from 'helpers/unitConversionHelpers'; // include for unit conversions
import useUomConvert from 'hooks/useUomConvert';
import useProduct from 'hooks/useProduct';
import useInventoryApi from 'hooks/useInventoryApi';
import { PRODUCT_APPLICATION_METHODS } from 'constants/products';
import {
  getCorrectUnitName,
  getCorrectUnitAbbreviation
} from 'helpers/unitsAbbreviation';
import {
  calculatePercentageOfAcres,
  calculateTotalOfAcres,
  parseMin
} from 'components/ProductSideBar/helpers/productHelper';
import { getApplicationType } from './helpers/applicationOptions';
import PriceUnitField from '../../../PriceUnitField';
import TargetPestAutocomplete from './components/TargetPestField';

const initialProductValues = values => ({
  totalProductValue: values.totalProductValue || 0,
  ratePerAreaValue: values.ratePerAreaValue || 0,
  ratePerAreaUnit: values.ratePerAreaUnit || values.stdunit || '',
  ratePerTankValue: values.ratePerTankValue || 0,
  ratePerTankUnit: values.ratePerTankUnit || values.stdunit || '',
  totalProductUnit: values.totalProductUnit || values.stdpackageunit || '',
  areaUnit: 'acre',
  appliedAreaValue: values.totalAreaValue || 0,
  coveragePercent: 100,
  appliedAreaUnit: 'acre',
  priceUnit:
    values.priceUnit ||
    values.averagePriceUnitAtTimeOfCreation ||
    values.stdpackageunit,
  averagePriceUnitAtTimeOfCreation:
    values.averagePriceUnitAtTimeOfCreation || values.stdpackageunit,
  averagePriceAtTimeOfCreation: values.averagePriceAtTimeOfCreation || 0,
  totalCostAtTimeOfCreation: values.totalCostAtTimeOfCreation || 0,
  specificCostPerUnit: values.specificCostPerUnit || 0,
  specificCostUnit: values.specificCostUnit || values.stdpackageunit,
  applicationMethod: values.applicationMethod || '',
  targetPest: values.targetPest,
  targetPestId: values.targetPestId,
  density: values?.density || values?.productDensity || values?.stdfactor || 0,
  productDensity:
    values?.density || values?.productDensity || values?.stdfactor || 0,

  shareOwnerInformation: {
    percentGrowerResponsibility: 100,
    totalFromGrowerInventoryQuantity: 100,
    totalFromGrowerInventoryUnit: ''
  },
  stdfactor: values.stdfactor || 1,
  stdpackageunit: values.stdpackageunit || '',
  stdunit: values.stdunit || values.stdUnit || '',
  custom: values.custom
});

const AddProductTaskModal = ({
  open,
  onClose,
  product = null,
  isEdit,
  prodIndex = 0,
  memberRole,
  memberFinancialAccess,
  selectedApplicationOption
}) => {
  const amplitude = useContext(AmplitudeContext);
  const flags = useFlags();
  const convertFn = useUomConvert();
  const orgId = Context?._currentValue[0]?.organization?.id;
  const isProductPriceFeature =
    flags.fePriceOverride &&
    (flags.beProductPriceListEnabled ||
      flags.beProductPriceListEnableByOrgId?.includes(orgId));
  const inventoryAPI = useInventoryApi('inventory/product');
  const [error, setError] = useState(null);
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const [appliedAreaChanged, setAppliedAreaChanged] = useState(false);
  const [appliedPercentageChange, setAppliedPercentageChange] = useState(false);
  const [productToAdd, setProductToAdd] = useState(initialProductValues({}));

  const [closedModal, setClosedModal] = useState(true);
  const [stdPackageUnit, setStdPackageUnit] = useState([]);

  const [toggleButtonEnabled, setToggleButtonEnabled] = useState(false);

  const { getMasterListProductUnits, getProductUnits } = useProduct();
  const ref = useRef(null);
  const [productListPrice, setProductListPrice] = useState({});
  const [
    unitPriceLoadedFromListPrice,
    setUnitPriceLoadedFromListPrice
  ] = useState(false);

  const isResetButtonDisabled =
    !productListPrice?.value ||
    (productListPrice.value === +productToAdd?.averagePriceAtTimeOfCreation &&
      productListPrice.unit === productToAdd?.averagePriceUnitAtTimeOfCreation);

  const canSaveNewListPrice =
    productListPrice?.value !== +productToAdd?.averagePriceAtTimeOfCreation ||
    productListPrice?.unit !== productToAdd?.averagePriceUnitAtTimeOfCreation;

  const toggleButtonExtraStyle = !canSaveNewListPrice
    ? { opacity: 0.5, pointerEvents: 'none' }
    : { opacity: 1, pointerEvents: 'inherit' };

  const gridWidth = isProductPriceFeature ? ' grid-cols-4 ' : ' grid-cols-3 ';
  const hideIfProductPriceDisabled = isProductPriceFeature ? ' ' : ' hidden ';
  const wideIfProductPriceEnabled = isProductPriceFeature
    ? ' col-span-2 '
    : ' ';
  useLayoutEffect(() => {
    if (ref.current) {
      if (isProductPriceFeature) {
        ref.current.closest(`[role=dialog]`).style.maxWidth = '672px';
      }
    }
  });

  useEffect(() => {
    if (closedModal || !productToAdd) {
      setProductToAdd(initialProductValues({}));
      setStdPackageUnit([]);
      setClosedModal(false);
    }
  }, [productToAdd, closedModal]);

  useEffect(() => {
    let newProduct = initialProductValues({});
    if (product) {
      newProduct = initialProductValues({
        ...product,
        applicationMethod: '',
        totalAreaValue: values?.totalAreaValue,
        density: values?.density
      });
    }

    setProductToAdd(newProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, values?.density]);

  const modalType = getApplicationType(selectedApplicationOption);

  const handleClose = () => {
    setClosedModal(true);
    onClose();
  };

  const calcProductValues = (
    valueChanged,
    targetValue,
    changedUnits,
    changedAppArea,
    newPrice
  ) => {
    const newProductToAdd = calculateProductValues(
      valueChanged,
      targetValue,
      changedUnits,
      changedAppArea,
      newPrice,
      productToAdd,
      { values, convertFn }
    );
    setProductToAdd(newProductToAdd);
  };

  const productUnitChange = unit => {
    const units = {
      rateUnit:
        modalType.key === 'ByRatePerArea'
          ? unit
          : productToAdd?.ratePerAreaUnit,
      tankUnit:
        modalType.key === 'ByRatePerTank'
          ? unit
          : productToAdd?.ratePerTankUnit,
      totalProdUnit:
        modalType.key === 'ByTotalProduct'
          ? unit
          : productToAdd?.totalProductUnit
    };
    calcProductValues(modalType.key, productToAdd?.[modalType.value], units);
  };

  const calcValuesAcresChange = (validatedValue, propertyChanged) => {
    const areaChanged = propertyChanged === 'area';

    const newAppliedPct = areaChanged
      ? calculatePercentageOfAcres(values?.totalAreaValue, validatedValue)
      : validatedValue;

    const newAppliedArea = !areaChanged
      ? calculateTotalOfAcres(values?.totalAreaValue, validatedValue)
      : validatedValue;

    const acresChangedValues = {
      changedPct: newAppliedPct,
      changedArea: newAppliedArea
    };

    calcProductValues(
      modalType.key,
      productToAdd?.[modalType.value],
      {},
      acresChangedValues
    );
  };

  const priceBagChange = (newPrice, newUnit) => {
    if (productToAdd && newUnit) {
      productToAdd.priceUnit = newUnit;
    }
    calcProductValues(
      modalType.key,
      productToAdd?.[modalType.value],
      {},
      {},
      newPrice
    );
  };

  const productAppliedAreaChange = e => {
    if (!appliedPercentageChange) {
      const validatedValue = parseMin(e.target.value, values?.totalAreaValue);
      calcValuesAcresChange(validatedValue, 'area');
    }
  };

  const productAppliedAreaPercentChange = e => {
    if (e.target.value) {
      if (!appliedAreaChanged) {
        const validatedValue = parseMin(e.target.value, 100);
        calcValuesAcresChange(validatedValue, 'pct');
      }
    }
  };
  const setProductValues = productValues => {
    const newProduct = initialProductValues(productValues);
    newProduct.density =
      productValues.density ||
      productValues.productDensity ||
      productValues.stdfactor;
    newProduct.totalProductValue = productValues.totalProductValue;
    newProduct.ratePerAreaValue = productValues.ratePerAreaValue;
    newProduct.ratePerTankValue = productValues.ratePerTankValue;
    newProduct.areaUnit = productValues.areaUnit;
    newProduct.appliedAreaValue = productValues.appliedAreaValue;
    newProduct.coveragePercent = productValues.coveragePercent;
    newProduct.appliedAreaUnit = productValues.appliedAreaUnit;
    newProduct.averagePriceUnitAtTimeOfCreation =
      productValues.averagePriceUnitAtTimeOfCreation;
    newProduct.averagePriceAtTimeOfCreation =
      productValues.averagePriceAtTimeOfCreation;
    newProduct.totalCostAtTimeOfCreation =
      productValues.totalCostAtTimeOfCreation;
    newProduct.specificCostPerUnit = productValues.specificCostPerUnit;
    newProduct.specificCostUnit = productValues.specificCostUnit;
    newProduct.applicationMethod = productValues.applicationMethod;
    newProduct.shareOwnerInformation = productValues.shareOwnerInformation;

    return newProduct;
  };

  useEffect(() => {
    const mappedProduct = isEdit
      ? setProductValues({
          ...product,
          applicationMethod: '',
          totalAreaValue: values?.totalAreaValue
        })
      : initialProductValues({
          ...product,
          applicationMethod: '',
          totalAreaValue: values?.totalAreaValue
        });
    if (isEdit) {
      setFieldValue('productUnit', product?.[modalType.unit]);
      setFieldValue('applicationMethod', product?.applicationMethod);
      if (product?.saveAsNewListPriceDate) {
        setToggleButtonEnabled(true);
      }
    } else {
      setFieldValue('productUnit', product?.stdunit);
      setFieldValue('applicationMethod', '');
    }
    setProductToAdd(mappedProduct);
    setAppliedAreaChanged(false);
    setAppliedPercentageChange(false);
  }, [
    isEdit,
    product,
    values?.totalAreaValue,
    modalType.unit,
    setFieldValue,
    values?.density
  ]);

  useEffect(() => {
    if (open && product) {
      if (!product.custom) {
        getMasterListProductUnits(product.id).then(setStdPackageUnit);
      } else {
        getProductUnits(product.id).then(setStdPackageUnit);
      }
      if (isProductPriceFeature) {
        inventoryAPI
          .fetch(product.id)
          .promise.then(productDataResponse => {
            setProductListPrice({
              value: productDataResponse.data.averagePricePerUnit,
              unit: productDataResponse.data.averagePricePerUnitUnit
            });
          })
          .catch(() => {
            setProductListPrice({});
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, product]);

  useEffect(() => {
    if (!open) return;
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.Tasks.addProductSuccess
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (
      open &&
      product &&
      productListPrice?.value &&
      !unitPriceLoadedFromListPrice
    ) {
      if (!isEdit) {
        setUnitPriceLoadedFromListPrice(true);
        setProductToAdd({
          ...productToAdd,
          averagePriceAtTimeOfCreation: productListPrice?.value || 0,
          averagePriceUnitAtTimeOfCreation:
            productListPrice?.unit ||
            productToAdd?.averagePriceUnitAtTimeOfCreation
        });
      }
    }
  }, [
    open,
    isEdit,
    product,
    productListPrice,
    productToAdd,
    unitPriceLoadedFromListPrice
  ]);

  const calcGrowerCost = () => {
    return (
      productToAdd?.totalCostAtTimeOfCreation *
      (productToAdd?.shareOwnerInformation?.percentGrowerResponsibility / 100)
    );
  };

  const getDisabled = () => {
    if (
      memberRole === 'Full control' &&
      (memberFinancialAccess === 'none' || memberFinancialAccess === 'READ')
    )
      return true;

    return false;
  };

  const displayTotalCostValue = () => {
    if (
      memberRole === 'Full control' &&
      memberFinancialAccess === 'none' &&
      isEdit
    ) {
      return true;
    }
    if (memberRole === 'Full control' && memberFinancialAccess === 'READ') {
      return false;
    }
    return false;
  };

  return (
    <>
      <FieldArray name="products">
        {({ push }) => (
          <PortalModal
            open={open}
            close={onClose}
            onCancel={() => {
              amplitude.sendEventToAmplitude(
                amplitude.events.epic.Tasks.cancelProduct
              );
              handleClose();
              amplitude.sendEventToAmplitude(
                amplitude.events.epic.Tasks.cancelProductSuccess
              );
            }}
            onConfirm={() => {
              amplitude.sendEventToAmplitude(
                amplitude.events.epic.Tasks.saveProduct
              );
              setFieldTouched('save product');
              if (!isEdit) {
                push({
                  ...productToAdd,
                  saveAsNewListPriceDate: toggleButtonEnabled
                    ? new Date().getTime()
                    : 0,
                  productName: product?.name,
                  manufacturerName: product?.manufacturer,
                  productId: product?.id
                });
              } else {
                values.products[prodIndex] = {
                  ...productToAdd,
                  saveAsNewListPriceDate: toggleButtonEnabled
                    ? new Date().getTime()
                    : 0,
                  productName: product?.name,
                  manufacturerName: product?.manufacturer,
                  productId: product?.id
                };
              }

              handleClose();
              amplitude.sendEventToAmplitude(
                amplitude.events.epic.Tasks.saveProductSuccess
              );
            }}
          >
            <div ref={ref} />
            {error && (
              <Toast icon="error" onClose={() => setError('')}>
                {error}
              </Toast>
            )}
            {!product ? (
              <Spinner />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="font-bold text-lg" data-testid="product-name">
                    {product.name}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div
                    className="text-sm text-neutral-300"
                    data-testid="manufacturer"
                  >
                    {product.manufacturer}
                  </div>
                </div>
                <div className="flex mb-6 mt-6">
                  {(selectedApplicationOption === 'ByTotalProduct' ||
                    selectedApplicationOption === 'ByRatePerTank') && (
                    <div className="mr-6">
                      <span className="text-sm text-neutral-300">
                        Rate/Area
                      </span>
                      <div>
                        <div className="text-xl">
                          {productToAdd?.ratePerAreaValue}{' '}
                          {getCorrectUnitAbbreviation(
                            productToAdd?.ratePerAreaUnit
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedApplicationOption !== 'ByTotalProduct' && (
                    <div className="mr-6">
                      <span className="text-sm text-neutral-300">
                        Total Product
                      </span>
                      <div className="text-xl" data-testid="total-product">
                        <FormattedNumber roundedTo={2}>
                          {Number(productToAdd?.totalProductValue)}
                        </FormattedNumber>{' '}
                        {getCorrectUnitAbbreviation(
                          productToAdd?.totalProductUnit
                        )}
                      </div>
                    </div>
                  )}
                  <div className="mr-6">
                    <span className="text-sm text-neutral-300">
                      Product Cost
                    </span>
                    <div className="text-xl">
                      <FormattedNumber unit="usCurrency" roundedTo={2}>
                        {productToAdd?.totalCostAtTimeOfCreation}
                      </FormattedNumber>
                    </div>
                  </div>
                </div>
                <div className="mt-12">
                  <div className={`grid ${gridWidth} gap-6 mb-12`}>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label={modalType.title}
                        name={modalType.value}
                        value={productToAdd?.[modalType.value]}
                        onChange={e => {
                          calcProductValues(
                            selectedApplicationOption,
                            e.target.value
                          );
                        }}
                        onFocus={e => e.target.select()}
                        imask={{
                          mask: Number,
                          radix: '.',
                          thousandsSeparator: ',',
                          normalizeZeros: false,
                          padFractionalZeros: false,
                          lazy: false,
                          scale: 10
                        }}
                      />
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        style={{ whiteSpace: 'nowrap' }}
                        control={Select}
                        label="Unit"
                        name="productUnit"
                        items={stdPackageUnit}
                        value={getCorrectUnitName(
                          productToAdd?.[modalType.unit]
                        )}
                        onChange={e => {
                          productUnitChange(JSON.parse(e.target.value).key);
                        }}
                      />
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label="Applied Area"
                        name="productAppliedArea"
                        value={productToAdd?.appliedAreaValue}
                        onFocus={() => setAppliedAreaChanged(true)}
                        onBlur={e => {
                          productAppliedAreaChange(e);
                          setAppliedAreaChanged(false);
                        }}
                        imask={{
                          mask: 'num \\ac',
                          lazy: false,
                          blocks: {
                            num: {
                              mask: Number,
                              radix: '.',
                              mapToRadix: [','],
                              thousandsSeparator: ','
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label="Applied Area %"
                        name="productAppliedAreaPercent"
                        value={productToAdd?.coveragePercent}
                        onFocus={() => setAppliedPercentageChange(true)}
                        onBlur={e => {
                          productAppliedAreaPercentChange(e);
                          setAppliedPercentageChange(false);
                        }}
                        imask={{
                          mask: 'num%',
                          lazy: false,
                          blocks: {
                            num: {
                              mask: Number,
                              radix: '.',
                              mapToRadix: [','],
                              thousandsSeparator: ','
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="my-2 col-span-2 ">
                      <div className="gr-h-md">
                        <PriceUnitField
                          units={stdPackageUnit}
                          onChange={priceBagChange}
                          defaultUnit={product.stdpackageunit}
                          object={productToAdd}
                          disabled={getDisabled()}
                          fieldNameInput="averagePriceAtTimeOfCreation"
                          fieldNameUnit="averagePriceUnitAtTimeOfCreation"
                        />
                      </div>
                    </div>
                    <div
                      className={`${hideIfProductPriceDisabled} my-2 w-full`}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <Button
                        label="Reset"
                        name="reset"
                        type="primary-ghost"
                        disabled={isResetButtonDisabled}
                        onClick={() => {
                          amplitude.sendEventToAmplitude(
                            amplitude.events.epic.ProductList.resetToListPrice,
                            { productId: product.id }
                          );
                          priceBagChange(
                            productListPrice.value,
                            productListPrice.unit
                          );
                        }}
                      >
                        Reset to List Price
                      </Button>
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label="Total Grower Cost"
                        name="totalGrowerCost"
                        value={
                          displayTotalCostValue()
                            ? ''
                            : calcGrowerCost().toFixed(2)
                        }
                        disabled
                      />
                    </div>
                    <div
                      style={toggleButtonExtraStyle}
                      className={`${hideIfProductPriceDisabled} mb-2 -mt-6 col-span-4`}
                    >
                      <div className="flex flex-row justify-left">
                        <span className="inline-block">
                          {!canSaveNewListPrice ? (
                            <ToggleButton
                              key="disabled"
                              id="disabledSavePriceList"
                              name="disabledSavePriceList"
                            />
                          ) : (
                            <ToggleButton
                              key="enabled"
                              id="savePriceList"
                              color="#0071CD"
                              name="savePriceList"
                              defaultChecked={toggleButtonEnabled}
                              onChange={e =>
                                setToggleButtonEnabled(e.currentTarget.checked)
                              }
                            />
                          )}
                        </span>
                        <label
                          htmlFor={
                            canSaveNewListPrice
                              ? 'savePriceList'
                              : 'disabledSavePriceList'
                          }
                          className="inline-block pl-2 -mt-2px text-sm"
                        >
                          Save as new list price
                        </label>
                      </div>
                      <div
                        className={`flex flex-row justify-left ${
                          toggleButtonEnabled && canSaveNewListPrice
                            ? ''
                            : 'invisible'
                        }`}
                      >
                        <img alt="info" src={infoIcon} className="mr-1 mt-1" />
                        <span className="text-sm mt-1">
                          This will not change any existing records
                        </span>
                      </div>
                    </div>
                    <div className={`my-2 ${wideIfProductPriceEnabled}`}>
                      <GrowUIFormField
                        control={Select}
                        label="Application Method"
                        name="applicationMethod"
                        items={PRODUCT_APPLICATION_METHODS}
                        onChange={e => {
                          setProductToAdd({
                            ...productToAdd,
                            applicationMethod: JSON.parse(e.target.value).key
                          });
                        }}
                      />
                    </div>
                    <div className="col-span-2 -mt-3">
                      <TargetPestAutocomplete
                        pest={product.targetPest}
                        onSelectPest={pest => {
                          setProductToAdd({
                            ...productToAdd,
                            targetPest: pest
                              ? {
                                  latinNames: pest.latinnames,
                                  name: pest.name,
                                  id: pest.nameid
                                }
                              : undefined
                          });
                        }}
                      />
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label="Grower Share"
                        name="growerShare"
                        value={
                          productToAdd?.shareOwnerInformation
                            ?.percentGrowerResponsibility
                        }
                        disabled
                        onChange={e => {
                          setProductToAdd({
                            ...productToAdd,
                            shareOwnerInformation: {
                              ...productToAdd?.shareOwnerInformation,
                              percentGrowerResponsibility: e.target.value
                            }
                          });
                        }}
                        imask={{
                          mask: 'num%',
                          lazy: false,
                          blocks: {
                            num: {
                              mask: Number,
                              radix: '.',
                              mapToRadix: [','],
                              thousandsSeparator: ','
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="my-2">
                      <GrowUIFormField
                        control={Input}
                        label="From Inventory"
                        name="fromInventory"
                        disabled
                        value={
                          productToAdd?.shareOwnerInformation
                            ?.totalFromGrowerInventoryQuantity
                        }
                        onChange={e => {
                          setProductToAdd({
                            ...productToAdd,
                            shareOwnerInformation: {
                              ...productToAdd?.shareOwnerInformation,
                              totalFromGrowerInventoryQuantity: e.target.value
                            }
                          });
                        }}
                        imask={{
                          mask: 'num%',
                          lazy: false,
                          blocks: {
                            num: {
                              mask: Number,
                              radix: '.',
                              mapToRadix: [','],
                              thousandsSeparator: ','
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </PortalModal>
        )}
      </FieldArray>
    </>
  );
};

AddProductTaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  product: PropTypes.object,
  isEdit: PropTypes.bool.isRequired,
  prodIndex: PropTypes.number,
  memberRole: PropTypes.string.isRequired,
  memberFinancialAccess: PropTypes.string.isRequired,
  selectedApplicationOption: PropTypes.string.isRequired
};

export default AddProductTaskModal;
